/**
 * This file defines the CodeEditor component, which is responsible for showing the code editor in the View and Grade Page.
 * Currently, the code editor does not work as Intended. Making and work and integrating with the whole grading process is a
 * future work.
 */
import log from "loglevel";
import React, { useEffect, useState, useRef } from "react";
import { Suspense, lazy } from 'react';
import * as constants from "Constants";
import httpService from 'services/httpService.js';
import { Spinner } from "react-bootstrap";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { Button, ButtonGroup, Modal } from "react-bootstrap";
import { IoMdDownload } from "react-icons/io";
import "./CodeEditor.styles.scss";
import JSZip from "jszip";
import "Screens1/Student/Course/Content/Programming/ComponentsNew/Testcase/TestCase.css"
import { getCommonEditorOptions } from "./monacoEditorOptions";
import { VscRunAll } from "react-icons/vsc";
import RunTerminal from "Screens1/Instructor/Course/Content/Programming/ComponentsNew/ManageSubmissions/RunTerminal.jsx";
import { CgRedo } from "react-icons/cg";
import { diffLines } from "diff";
import { FaCodeCompare } from "react-icons/fa6";
import { FaFileCode } from "react-icons/fa6";

const Editor = lazy(() => import('@monaco-editor/react'));
const editorOptions = getCommonEditorOptions();

function CodeEditor({
  programId,
  submission,
  fileContent,
  submissionPath,
  setIsEditEnabled,
  activityTitle
}) {
  console.log("submission", submission);
  //useRef variable to store current editor instance
  const editorRef = useRef(null);

  //variable to store current language in editor
  const language = submission?.submission_program_language || "C++";

  //variable for storing test case results
  const [testCaseResults, setTestCaseResults] = useState([]);

  //variable to store current code displayed inside code editor
  const [code, setCode] = useState(() => {
    const initial = {};
    fileContent.forEach(file => {
      const nameWithoutExtension = file.name.replace(/\.[^/.]+$/, '');
      initial[nameWithoutExtension] = file.content;
    });
    return initial;
  });

  //variable to store original code submitted by student
  const [originalCode, setOriginalCode] = useState({})

  //variable for current file tab
  const [activeTab, setActiveTab] = useState(0);

  //variable for current file in editor
  const [currentFileInEditor, setCurrentFileInEditor] = useState(fileContent[0]?.name);

  //variable for handling sending editor data to backend after every 5 seconds
  const [lastSavedTime, setLastSavedTime] = useState(new Date());

  //useRef variable for comparing code after every 5 seconds 
  const prevEditorTextRef = useRef(code);

  //boolean variable for showing modal of compare modified code
  const [showCompareModal, setShowCompareModal] = useState(false);

  //usestate variable to store changes done by TA in student submission
  const [diff, setDiff] = useState(null);
  
  //States for custom input feature
  //================================================================================================
  const [errorOnRun, setErrorOnRun] = useState("");
  const [successOnRun, setSuccessOnRun] = useState(false);
  const [runToggle, setRunToggle] = useState(false);
  const [isRunLoading, setIsRunLoading] = useState(false);
  const [showCustomTerminal, setShowCustomTerminal] = useState(false);
  const [compiling, setCompiling] = useState(false);
  const [sessionKey, setSessionKey] = useState("");

  //useeffect for fetching student submitted code and handle corresponding states
  useEffect(() => {

    const fetchEditorData = async () => {
      // Construct the URL for fetching the editor data
      const url = constants.base_url + "/evalpro/editor/" + programId// + "/" + encodeURIComponent(submission?.email || "");
      
      const submittedfile = {};
      let extension;
      fileContent.forEach(file => {
        // Remove file extension from the name
        const nameWithoutExtension = file.name.replace(/\.[^/.]+$/, "");
        extension = file.name.split(".")[1];
        submittedfile[nameWithoutExtension] = file.content;
      });
      setOriginalCode(submittedfile);
      try {
        const response =
          
            await httpService.get(url, {
            headers: {
              "Content-Type": "application/pdf",
            },
            params: {
              "mail": submission?.email,
 
            },
          })
        const data = response.data;
        setSessionKey(response.data.editor_buffer.session_key);
        const taBuffer = data.editor_buffer?.ta_buffer_data;
        if (taBuffer && typeof taBuffer === 'object' && Object.keys(taBuffer).length > 0) {
            setCode(taBuffer);
        }
      } catch (error) {
        log.error("Error fetching editor data:", error);
      }
    }

    fetchEditorData();
  }, [submission]);

  //This useEffect is used to set the filecontnt value using setdiff for diff comparison
  useEffect(() => {
    const allDiffs = {};
    // Compare each file
    Object.keys(code)?.forEach(filename => {
      const taContent = code[filename] || '';
      const studentContent = originalCode[filename] || '';
      allDiffs[filename] = diffLines(studentContent, taContent);
    });
    setDiff(allDiffs);
  }, [code, originalCode, currentFileInEditor, submission])

  //This useEffect is used to send editer buffer after every 5 seconds.
  useEffect(() => {
    const currentTime = new Date();
    const stripFileExtensions = (codeObj) => {
      const newCodeObj = {};
      for (const key in codeObj) {
        const keyWithoutExt = key.split('.')[0];
        newCodeObj[keyWithoutExt] = codeObj[key];
      }
      return newCodeObj;
    };
    // If more than 5 seconds have passed since the last save
    if (code && typeof code === 'object' && Object.keys(code).length > 0 && currentTime - lastSavedTime > 5000) {
      const url = constants.base_url + "/evalpro/editor/edit_ta_buffer/" + programId;
      const form_data = new FormData();
      const strippedCode = stripFileExtensions(code);
      form_data.append("ta_buffer_data", JSON.stringify(strippedCode));
      form_data.append("session_key", sessionKey);
      form_data.append("Student_Email", submission?.email);
      httpService
        .put(url, form_data, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then(() => {
          // Update 'prevEditorTextRef' with the current 'code' state
          prevEditorTextRef.current = code;
        });

      // Update 'lastSavedTime' with the current time
      setLastSavedTime(currentTime);
    }

    // Set an interval to check for changes in 'code' every 5 seconds
    const interval = setInterval(() => {
      // If less than or equal to 5 seconds have passed since the last save
      // and 'code' has changed since the last save
      if (
        code && typeof code === 'object' && Object.keys(code).length > 0 &&
        currentTime - lastSavedTime <= 5000 &&
        prevEditorTextRef.current !== code
      ) {
        const url = constants.base_url + "/evalpro/editor/edit_ta_buffer/" + programId;
        const form_data = new FormData();
        const strippedCode = stripFileExtensions(code);
        form_data.append("ta_buffer_data", JSON.stringify(strippedCode));
        form_data.append("session_key", sessionKey);
        form_data.append("Student_Email", submission?.email);
        httpService
          .put(url, form_data, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then((response) => {
            prevEditorTextRef.current = code;
          });

        // Update 'lastSavedTime' with the current time
        setLastSavedTime(currentTime);
      }
    }, 5000);

    //clearing interval after component is removed from dom 
    return () => clearInterval(interval);
  }, [code, lastSavedTime, sessionKey, submission]);

  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;
  }

  //Function to handle custom input
  const onRunCode = async (e) => {
    try {
      setIsRunLoading(true);
      setRunToggle(!runToggle);
      const url =
        constants.base_url +
        "/evalpro/editor/check_compilation_status/" +
        programId;
      const form_data = new FormData();
      form_data.append("programming_language", language);
      form_data.append("key", "ta_buffer_data");
      form_data.append("Student_Email", submission?.email);
      const response = await httpService
        .put(url, form_data, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })

      if (response.data.result.status === "fail") {
        setErrorOnRun(response.data.result.error);
        setSuccessOnRun(false);
      } else {
        setErrorOnRun("");
        setSuccessOnRun(true);
      }
      setIsRunLoading(false);
      setShowCustomTerminal(true);
    } catch (error) {
      log.error(error);
    } finally {
      setIsRunLoading(false);
      setCompiling(false);
    }
  };

  //function to download code when download button is clicked
  const downloadCode = () => {
    if (fileContent.length === 1) {
      // Single file download
      const file = fileContent[0];
      const blob = new Blob([file.content], { type: "text/plain" });
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = currentFileInEditor;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } else {
      // Multiple files: Create ZIP
      const zip = new JSZip();
      fileContent.forEach(({ name, content }) => {
        zip.file(name, content);
      });

      zip.generateAsync({ type: "blob" }).then((zipBlob) => {
        const zipUrl = URL.createObjectURL(zipBlob);
        const a = document.createElement("a");
        a.href = zipUrl;
        a.download = `${activityTitle}.zip`;
        document.body.appendChild(a);
        a.click();

        // Cleanup
        document.body.removeChild(a);
        URL.revokeObjectURL(zipUrl);
      });
    }
  };

  //function to keep track of all the changes done in code editor
  const handleEditorChange = (value) => {
    const updatedEditorText = { ...code };
    updatedEditorText[currentFileInEditor.split('.')[0]] = value;
    setCode(updatedEditorText);
  }

  //function to handle view student submitted code button
  const handleSaveCode = () => {
    const obj = {};
    if (!code || Object.keys(code).length === 0) {
      fileContent?.forEach((file) => {
        obj[file.name] = file.content;
      });
      setCode(obj);
    }
    setIsEditEnabled(false);
  };

  //fucntion to handle selection of tabs in code editor
  const handleClick = (id) => {
    setActiveTab(id);
    setCurrentFileInEditor(fileContent[id]?.name);
  };

  //Handling the testcase result
  const [testcaseResult, setTestCaseResult] = useState({
    status: 0,
    testCases: [
      {
        status: 1,
        message: "Passed",
        input: "14",
        expected: "123",
        output: "123",
      },
      {
        status: 1,
        message: "Failed",
        input: "15",
        expected: "456",
        output: "1",
      },
    ],
  });
  
  return (
    <div className="w-100 cont flex flex-column">
      <div className="tabs" style={{ overflow: "visible" }}>
        <ul className="nav nav-tabs sticky-tabs">
          {(fileContent)?.map((file, index) => (
            <li key={index} className="nav-item">
              <a
                className={`nav-link ${activeTab === index ? "active" : ""}`}
                onClick={() => handleClick(index)}
                style={{ cursor: "pointer" }}
              >
                {file.name}
              </a>
            </li>
          ))}

          <ButtonGroup className=" d-flex justify-content-between ml-auto ">
            <OverlayTrigger
              overlay={<Tooltip>Reset Code</Tooltip>}
            >
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  if (confirm("Are you sure you want to reset your code?")) {
                    console.log("original code", originalCode)
                    setCode(originalCode);
                  }
                }}
                style={{ height: '30px', marginTop: '10px', border: "black", borderRadius: '5px' }}
              >
                <CgRedo />
              </Button>
            </OverlayTrigger>

            <OverlayTrigger
              overlay={<Tooltip>Student submitted code</Tooltip>}
            >
              <Button
                variant="secondary" size="sm"
                onClick={() => handleSaveCode()}
                style={{ border: "black", marginLeft: '10px', height: '30px', marginTop: '10px', borderRadius: '5px' }}
              >
                <FaFileCode />
              </Button>
            </OverlayTrigger>

            <OverlayTrigger
              overlay={<Tooltip>Download</Tooltip>}
            >
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  downloadCode()
                }}
                style={{ cursor: 'pointer', marginLeft: '10px', height: '30px', marginTop: '10px', borderRadius: '5px' }}
              >
                <IoMdDownload />
              </Button>
            </OverlayTrigger>

            <OverlayTrigger
              overlay={<Tooltip>View Diff</Tooltip>}
            >
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setShowCompareModal(true)
                }}
                style={{ cursor: 'pointer', marginLeft: '10px', marginRight: '10px', height: '30px', marginTop: '10px', borderRadius: '5px' }}
              >
                <FaCodeCompare />
              </Button>
            </OverlayTrigger>
          </ButtonGroup>
        </ul>

        <div
          className="tab-content"
          style={{ overflow: "auto" }}
        >
          {(fileContent)?.map((file, index) => (
            <div
              key={index}
              className={`tab-pane ${activeTab === index ? "active" : ""}`}
              style={{ height: '100%', maxHeight: '500px', border: "1px solid gray", overflow: 'auto', marginBottom: '10px', position: 'relative', zIndex: '100' }}
            >
              <Suspense fallback={
                <div className="d-flex justify-content-center align-items-center w-100">
                  <Spinner size="sm" />
                </div>
              }>
                <Editor
                  theme={"light"}
                  width="100%"
                  language="cpp"
                  value={code[currentFileInEditor.split('.')[0]]}
                  options={editorOptions}
                  onMount={handleEditorDidMount}
                  onChange={handleEditorChange}
                  style={{
                    boxShadow: "0 1px 4px rgba(0, 0, 0, .1)",
                    height: '500px'
                  }}
                />
              </Suspense>
            </div>
          ))}
        </div>
      </div>

      <div style={{ flex: '2', position: "relative", maxWidth: '100%', marginBottom: '10px' }}>
        <div
          style={{ display: 'flex', justifyContent: 'flex-end' }}
        >
          <OverlayTrigger overlay={<Tooltip>Run Without Submit</Tooltip>}>
            <Button
              variant="primary"
              onClick={(e) => {
                onRunCode(e);
                setCompiling(true);
              }
              }
              style={{ position: "relative", marginTop: '10px', marginRight: '10px', border: "1px solid gray" }}
            >
              Run&nbsp;<VscRunAll size={24} />
            </Button>
          </OverlayTrigger>
        </div>
        {compiling &&
          (
            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <p style={{
                color: 'white',
                backgroundColor: 'gray',
                padding: '10px 15px',
                borderRadius: '8px',
                fontWeight: 'bold',
                display: 'inline-flex',
                alignItems: 'center',
                fontFamily: 'sans-serif',
                fontSize: '16px'
              }}>
                <span className="spinner" style={{
                  width: '14px',
                  height: '14px',
                  border: '2px solid #fff',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  marginRight: '8px',
                  animation: 'spin 1s linear infinite'
                }}></span>
                Compilation is in progress...
              </p>
            </div>
          )}
        <div
          style={{ marginTop: '10px', padding: '1px' }}
        >
          <RunTerminal
            errorOnRun={errorOnRun}
            programId={programId}
            language={language}
            runToggle={runToggle}
            successOnRun={successOnRun}
            setRunToggle={setRunToggle}
            setErrorOnRun={setErrorOnRun}
            setSuccessOnRun={setSuccessOnRun}
            isRunLoading={isRunLoading}
            showCustomTerminal={showCustomTerminal}
            setShowCustomTerminal={setShowCustomTerminal}
            submission={submission}
          />
        </div>

      </div>
      {showCompareModal &&
        <div key={currentFileInEditor}>

          <Modal
            show={showCompareModal}
            onHide={() => setShowCompareModal(false)}
            aria-labelledby="contained-modal-title-vcenter"
            size="xl"
            centered
          >
            <Modal.Header
              style={{
                position: "sticky",
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                top: 0,
                backgroundColor: "white",
                zIndex: 1051,
                padding: "10px 20px"
              }}
            >
              <span style={{
                fontWeight: 'bold',
                fontSize: "20px",
                textAlign: 'center',
                flex: 1,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                Compare Modified Code
              </span>

              <Button
                variant="danger"
                size="sm"
                onClick={() => setShowCompareModal(false)}
                style={{ marginLeft: "10px" }}
              >
                Close
              </Button>
            </Modal.Header>

            <Modal.Body style={{
              borderRadius: "10px",
              background: "#FFF",
              boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
              maxHeight: "70vh",
              overflowY: "auto",
              padding: "20px"
            }}>

              {diff && Object.keys(diff).length > 0 ? (
                Object.entries(diff).map(([filename, fileDiff], index) => {
                  // Compute line stats for this file
                  let addedCount = 0, removedCount = 0;

                  fileDiff.forEach(part => {
                    const lines = part.value.split("\n").filter(line => line.trim() !== '').length;
                    if (part.added) addedCount += lines;
                    if (part.removed) removedCount += lines;
                  });

                  return (
                    <div key={index} style={{ marginBottom: "30px" }}>
                      <h5 style={{ fontWeight: 'bold' }}>{filename}.{currentFileInEditor?.split('.')[1] || 'cpp'}</h5>
                      <div style={{ marginBottom: "10px", fontWeight: "bold" }}>
                        <span style={{ color: "green", marginRight: "15px" }}>+{addedCount} lines</span>
                        <span style={{ color: "red", marginRight: "15px" }}>-{removedCount} lines</span>
                      </div>

                      <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '10px',
                        border: '1px solid #ccc',
                        borderRadius: '8px',
                        padding: '10px'
                      }}>
                        {/* Student side */}
                        <div style={{
                          flex: 1, backgroundColor: '#f4f4f4', padding: '10px',
                          overflow: 'auto', maxHeight: '300px', border: '1px solid black', borderRadius: "10px"
                        }}>
                          <div style={{ textAlign: 'center', fontWeight: 'bold' }}>Student Submission</div>
                          <hr />
                          {fileDiff.map((part, i) => {
                            if (part.removed) {
                              return <pre key={i} style={{ color: 'red', margin: 0 }}>{part.value}</pre>;
                            } else if (!part.added) {
                              return <pre key={i} style={{ color: 'black', margin: 0 }}>{part.value}</pre>;
                            }
                            return null;
                          })}
                        </div>

                        {/* TA side */}
                        <div style={{
                          flex: 1, backgroundColor: '#f4f4f4', padding: '10px',
                          overflow: 'auto', maxHeight: '300px', border: '1px solid black', borderRadius: "10px"
                        }}>
                          <div style={{ textAlign: 'center', fontWeight: 'bold' }}>TA Modified Code</div>
                          <hr />
                          {fileDiff.map((part, i) => {
                            if (part.added) {
                              return <pre key={i} style={{ color: 'green', margin: 0 }}>{part.value}</pre>;
                            } else if (!part.removed) {
                              return <pre key={i} style={{ color: 'black', margin: 0 }}>{part.value}</pre>;
                            }
                            return null;
                          })}
                        </div>
                      </div>
                      <hr />
                    </div>
                  );
                })
              ) : (
                <div>No differences to show.</div>
              )}
            </Modal.Body>
          </Modal>
        </div>
      }
    </div>
  );
}
export default CodeEditor;
